import { Link } from "wouter";
import { PenLine } from "lucide-react";

type Row = {
  provider: string;
  url: string;
  purpose: string;
  data: string;
  certifications: string;
  region: string;
};

const SUBPROCESSORS: Row[] = [
  {
    provider: "Neon, Inc.",
    url: "https://neon.com/privacy-policy",
    purpose: "Managed PostgreSQL database for application data.",
    data: "All ClassTrack application data: profiles, classes, students, records, diary entries, markbook scores, tasks, and notes.",
    certifications: "SOC 2 Type II.",
    region: "Australia (AWS ap-southeast-2, Sydney).",
  },
  {
    provider: "Amazon Web Services, Inc. (AWS)",
    url: "https://aws.amazon.com/privacy/",
    purpose: "Object storage for uploaded files, including lesson and diary images and imported program documents.",
    data: "Teacher-uploaded files: lesson images, diary images, and program Word/PDF documents. Files are stored in private object storage and served only to authenticated users via the ClassTrack API server. No student PII is written to object key names by design.",
    certifications: "ISO 27001, SOC 1/2/3, PCI DSS Level 1, IRAP assessed.",
    region: "Australia (AWS ap-southeast-2, Sydney).",
  },
  {
    provider: "Replit, Inc.",
    url: "https://replit.com/site/privacy",
    purpose: "Backend API server hosting through Replit Autoscale.",
    data: "Request/response traffic to the API server and operational logs. Production deployment geography is Australia. No student PII is written to logs by design.",
    certifications: "SOC 2 Type II.",
    region: "Australia.",
  },
  {
    provider: "Clerk, Inc.",
    url: "https://clerk.com/privacy",
    purpose: "User authentication and session management.",
    data: "Account email, name, hashed password, OAuth identifiers, sign-in metadata (IP, device fingerprint).",
    certifications: "SOC 2 Type II.",
    region: "United States.",
  },
  {
    provider: "Stripe, Inc.",
    url: "https://stripe.com/au/privacy",
    purpose: "Subscription billing and card processing.",
    data: "Email, billing name and address, card details (handled directly by Stripe — never stored on ClassTrack servers), subscription state.",
    certifications: "PCI DSS Level 1, SOC 2 Type II.",
    region: "United States (with global card-network routing).",
  },
  {
    provider: "OpenAI, L.L.C.",
    url: "https://openai.com/policies/privacy-policy",
    purpose: "Powers the optional AI note-polishing and lesson-preparation features.",
    data: "Redacted prompt text only. Student names are replaced with stable placeholders (e.g. \"Student A\"); emails, phone numbers, dates, addresses, NDIS / Medicare numbers, parent/carer references, diagnoses and medication names are replaced with neutral tokens before the request leaves ClassTrack. Prompts are not retained for training under OpenAI's API data policy.",
    certifications: "SOC 2 Type II.",
    region: "United States.",
  },
  {
    provider: "Functional Software, Inc. d/b/a Sentry",
    url: "https://sentry.io/privacy/",
    purpose: "Error and performance diagnostics so we can detect and fix outages.",
    data: "Stripped of authentication credentials, session cookies, student names and IDs, names, email addresses, phone numbers, and other personally identifying fields before leaving our servers. Each report retains only the Clerk user identifier, the URL path, and a non-identifying short school code.",
    certifications: "SOC 2 Type II, ISO 27001.",
    region: "United States.",
  },
  {
    provider: "Vercel Inc.",
    url: "https://vercel.com/legal/privacy-policy",
    purpose: "Static frontend hosting (compiled JavaScript bundle, CSS, and images).",
    data: "No student data. Vercel serves the public JavaScript bundle and routes /api/* to the ClassTrack backend.",
    certifications: "SOC 2 Type II, ISO 27001.",
    region: "Global edge (frontend assets only).",
  },
];

const AU_BADGE = (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
    🇦🇺 Australia
  </span>
);

const US_BADGE = (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
    🇺🇸 United States
  </span>
);

const GLOBAL_BADGE = (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
    🌍 Global edge
  </span>
);

function regionBadge(region: string) {
  if (region.startsWith("Australia")) return AU_BADGE;
  if (region.startsWith("Global")) return GLOBAL_BADGE;
  return US_BADGE;
}

export default function Subprocessors() {
  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <header className="px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <Link href="/" className="flex items-center gap-2">
          <PenLine className="h-6 w-6 text-primary" />
          <span className="text-xl font-bold tracking-tight text-card-foreground">ClassTrack</span>
        </Link>
      </header>

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Subprocessors</h1>
        <p className="text-sm text-gray-400 mb-8">Last updated: June 2026</p>

        {/* Summary card */}
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-4 mb-8 text-sm text-emerald-900 leading-relaxed space-y-2">
          <p>
            ClassTrack stores core application data and uploaded files in Australia. The production database is hosted by Neon on AWS Sydney, uploaded files are stored in private AWS S3 object storage in Sydney, and the production API server runs on Replit Autoscale with Australian deployment geography.
          </p>
          <p>
            Some supporting services are provided by trusted overseas or global providers, including authentication, payments, optional AI features, error monitoring, and static frontend delivery. Each provider is listed below with its purpose, data handled, and region.
          </p>
        </div>

        <p className="text-sm text-gray-500 mb-8 leading-relaxed">
          ClassTrack relies on the third-party service providers listed below ("subprocessors") to operate the service. Each subprocessor processes only the data described in its row, for the purpose described, and is bound by its own published privacy policy. Card details are handled directly by Stripe and are never stored on ClassTrack servers.{" "}
          For the broader hosting context see our{" "}
          <Link href="/privacy" className="text-primary underline">Privacy Policy</Link>.
        </p>

        <div className="space-y-4">
          {SUBPROCESSORS.map((row) => (
            <article
              key={row.provider}
              className="rounded-lg border border-gray-200 bg-white overflow-hidden"
            >
              <div className="flex items-baseline justify-between flex-wrap gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="text-base font-semibold text-gray-900">{row.provider}</h2>
                <div className="flex items-center gap-3">
                  {regionBadge(row.region)}
                  <a
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary underline"
                  >
                    Privacy policy ↗
                  </a>
                </div>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm text-gray-700 px-5 py-3">
                <dt className="font-medium text-gray-500 text-xs uppercase tracking-wide pt-0.5">Purpose</dt>
                <dd>{row.purpose}</dd>
                <dt className="font-medium text-gray-500 text-xs uppercase tracking-wide pt-0.5">Data handled</dt>
                <dd>{row.data}</dd>
                <dt className="font-medium text-gray-500 text-xs uppercase tracking-wide pt-0.5">Certifications</dt>
                <dd>{row.certifications}</dd>
                <dt className="font-medium text-gray-500 text-xs uppercase tracking-wide pt-0.5">Region</dt>
                <dd>{row.region}</dd>
              </dl>
            </article>
          ))}
        </div>

        <section className="mt-10 text-sm text-gray-600 leading-relaxed space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Updates to this list</h2>
          <p>
            We will update this page when we add, remove, or replace a subprocessor. Material changes that affect the regions where data is processed will also be reflected in our{" "}
            <Link href="/privacy" className="text-primary underline">Privacy Policy</Link>.
          </p>
          <p>
            For questions about this list, contact{" "}
            <a href="mailto:classtrack.com.au@gmail.com" className="text-primary underline">classtrack.com.au@gmail.com</a>.
          </p>
          <p className="text-xs text-gray-400">
            ClassTrack is operated by The trustee for The ASSIDA Unit Trust, ABN 26 760 767 802, trading as ClassTrack.
          </p>
        </section>
      </main>

      <footer className="bg-gray-900 text-gray-400 py-8 px-6 text-center text-sm">
        <div className="flex justify-center gap-6 mb-3">
          <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link href="/legal/subprocessors" className="hover:text-white transition-colors">Subprocessors</Link>
        </div>
        <p>&copy; 2026 ClassTrack. All rights reserved.</p>
      </footer>
    </div>
  );
}
